
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
    LayoutDashboard, 
    Building2, 
    Users, 
    LogOut, 
    Menu, 
    X, 
    ChevronLeft,
    ChevronRight,
    FileText,
    Gauge,
    BookOpen,
    DollarSign
} from 'lucide-react';
import Modal from '../components/ui/Modal';

// Componente Sidebar Item
const NavItem = ({ to, icon: Icon, label, active, collapsed }) => (
  <Link
    to={to}
    title={collapsed ? label : ''}
    className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
      active 
        ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-600' 
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    } ${collapsed ? 'justify-center px-2' : ''}`}
  >
    <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} ${active ? 'text-blue-600' : 'text-gray-400'}`} />
    {!collapsed && label}
  </Link>
);

const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
      // Menu para Super Admin - Ve TODO
      ...(user?.isSuperAdmin || user?.esSuperAdmin ? [
          { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { to: '/admin/empresas', icon: Building2, label: 'Juntas de Agua' },
          { to: '/admin/usuarios', icon: Users, label: 'Usuarios' },
          { to: '/admin/tarifas', icon: FileText, label: 'Tarifas' },
          { to: '/admin/clientes', icon: Users, label: 'Clientes' },
          { to: '/admin/medidores', icon: Gauge, label: 'Medidores' },
          { to: '/admin/consumos', icon: FileText, label: 'Consumos' },
          { to: '/admin/deudas', icon: DollarSign, label: 'Deudas Extras' },
          { to: '/admin/reportes', icon: FileText, label: 'Reportes' },
          { to: '/admin/rendicioncuentas', icon: BookOpen, label: 'Libro Contable' },
      ] : [
          // Menu para Admin de Empresa - Solo su empresa
          { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          // Solo Admin y Presidente pueden ver Gestión de Usuarios
          ...(
            (user?.roles?.some(r => ['Administrador', 'Presidente', 'A', 'P'].includes(typeof r === 'object' ? r.nombre : r))) ||
            (['Administrador', 'Presidente', 'A', 'P'].includes(typeof user?.rol === 'object' ? user?.rol?.nombre : user?.rol))
            ? [
            { to: '/admin/usuarios', icon: Users, label: 'Usuarios' }
          ] : []),
          { to: '/admin/tarifas', icon: FileText, label: 'Tarifas' },
          { to: '/admin/clientes', icon: Users, label: 'Clientes' },
          { to: '/admin/medidores', icon: Gauge, label: 'Medidores' },
          { to: '/admin/consumos', icon: FileText, label: 'Consumos' },
          { to: '/admin/pagos', icon: FileText, label: 'Pagos' },
          { to: '/admin/deudas', icon: DollarSign, label: 'Deudas Extras' },
          { to: '/admin/reportes', icon: FileText, label: 'Reportes' },
          { to: '/admin/rendicioncuentas', icon: BookOpen, label: 'Libro Contable' },
          ...(
            (user?.roles?.some(r => ['Administrador', 'Presidente', 'Secretario', 'A', 'P', 'S'].includes(typeof r === 'object' ? r.nombre : r))) ||
            (['Administrador', 'Presidente', 'Secretario', 'A', 'P', 'S'].includes(typeof user?.rol === 'object' ? user?.rol?.nombre : user?.rol))
            ? [
              { to: '/admin/mi-empresa', icon: Building2, label: 'Mi Empresa' }
            ] : []),
      ]),
  ];

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transform transition-all duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-16' : 'lg:w-56'}
        w-64 flex-shrink-0 flex flex-col h-full
      `}>
        {/* Logo Area */}
        <div className={`h-16 flex items-center border-b border-gray-100 ${isCollapsed ? 'justify-center px-2' : 'px-6'}`}>
           <div className="flex items-center space-x-2 overflow-hidden">
                <div className="w-8 h-8 min-w-[2rem] bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                    SA
                </div>
                {!isCollapsed && (
                    <span className="text-xl font-bold text-gray-800 whitespace-nowrap transition-opacity duration-300">
                        SaaS Agua
                    </span>
                )}
           </div>
           
           {/* Desktop Toggle Button */}
           {!isCollapsed && (
                <button
                    onClick={() => setIsCollapsed(true)}
                    className="hidden lg:flex ml-auto p-1.5 rounded-md bg-gray-50 text-gray-500 hover:bg-gray-100 focus:outline-none"
                    title="Colapsar"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
           )}

           {isCollapsed && (
                <button
                    onClick={() => setIsCollapsed(false)}
                    className="hidden lg:flex p-1.5 rounded-md bg-gray-50 text-gray-500 hover:bg-gray-100 focus:outline-none absolute -right-3 top-6 z-50 shadow-sm border border-gray-200 bg-white"
                    title="Expandir"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
           )}
           
           {/* Mobile Close Button */}
           <button 
             className="ml-auto lg:hidden text-gray-500"
             onClick={() => setIsSidebarOpen(false)}
           >
             <X className="w-6 h-6" />
           </button>
        </div>


        {/* Navigation */}
        <nav className="mt-2 space-y-1 flex-1">
            {menuItems.map((item) => (
                <NavItem 
                    key={item.to}
                    {...item}
                    active={location.pathname === item.to}
                    collapsed={isCollapsed}
                />
            ))}
        </nav>
      </aside>

      {/* Logout Confirmation Modal */}
      <Modal
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
          title="¿Cerrar Sesión?"
          size="sm"
          footer={
              <>
                  <button
                      onClick={handleLogout}
                      className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                      Cerrar Sesión
                  </button>
                  <button
                      onClick={() => setIsLogoutModalOpen(false)}
                      className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                      Cancelar
                  </button>
              </>
          }
      >
          <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <LogOut className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-gray-600">
                  ¿Estás seguro de que deseas salir del sistema? Deberás ingresar tus credenciales nuevamente.
              </p>
          </div>
      </Modal>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 lg:px-8">
            <div className="flex items-center">
                <button 
                    className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-md mr-2"
                    onClick={() => setIsSidebarOpen(true)}
                >
                    <Menu className="w-6 h-6" />
                </button>

                <div className="flex items-center text-sm text-gray-500">
                    <span className="hidden sm:inline">Portal de Gestión</span>
                    <ChevronRight className="w-4 h-4 mx-2" />
                    <span className="font-medium text-gray-900">{title}</span>
                </div>
            </div>
            
            {/* Header Right Actions */}
            <div className="flex items-center gap-4 relative">
                <div className="relative">
                    <button 
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                        {user?.foto ? (
                            <img 
                                src={`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${user.foto}`} 
                                alt={user.nombre || user.usuario}
                                className="w-8 h-8 rounded-full object-cover border border-gray-200"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                                {(user?.nombre || user?.usuario || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <span className="text-sm font-medium text-gray-700 hidden sm:block">
                            {user?.nombre || user?.usuario}
                        </span>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isProfileOpen && (
                        <>
                            {/* Backdrop to close on click outside */}
                            <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setIsProfileOpen(false)}
                            ></div>
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 transform origin-top-right transition-all">
                                <Link 
                                    to="/admin/perfil" 
                                    onClick={() => setIsProfileOpen(false)}
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                                >
                                    Ver Perfil
                                </Link>
                                <div className="h-px bg-gray-100 my-1"></div>
                                <button 
                                    onClick={() => {
                                        setIsProfileOpen(false);
                                        setIsLogoutModalOpen(true);
                                    }}
                                    className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                    <LogOut className="w-4 h-4" /> Cerrar Sesión
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8">
            <div className="w-full mx-auto">
                {children}
            </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
